import { Link } from '@inertiajs/react'
import AccountLayout from '@/layouts/AccountLayout'
import PageHeader from '@/components/PageHeader'
import GettingStartedChecklist from '@/components/GettingStartedChecklist'
import type { OnboardingChecklist, SurveyListItem } from '@/types'
import { SURVEY_TYPE_LABELS } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface SurveysProps {
  surveys: SurveyListItem[]
  checklist?: OnboardingChecklist | null
}

const STATUS_BADGE: Record<SurveyListItem['status'], string> = {
  draft: 'badge badge-warning badge-sm',
  active: 'badge badge-success badge-sm',
  closed: 'badge badge-error badge-sm',
}

const STATUS_LABEL: Record<SurveyListItem['status'], string> = {
  draft: 'Draft',
  active: 'Active',
  closed: 'Closed',
}


function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export default function Surveys({ surveys, checklist }: SurveysProps) {
  const ap = useAccountPath()
  const hasSurveys = surveys.length > 0

  return (
    <AccountLayout>
      <div className="space-y-6">
        <PageHeader
          title="Surveys"
          actions={hasSurveys ? (
            <Link href={ap('/surveys/new')} className="btn btn-primary btn-sm">
              <span className="iconify lucide--plus size-4" />
              New Survey
            </Link>
          ) : undefined}
        />

        {checklist && !checklist.dismissed && (
          <GettingStartedChecklist checklist={checklist} surveys={surveys} />
        )}

        {hasSurveys ? (
          <div className="card bg-base-100">
            <div className="overflow-x-auto">
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Programme</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th className="text-end">Responses</th>
                    <th>Created</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {surveys.map((survey) => (
                    <tr key={survey.id} className="hover:bg-base-200/40">
                      <td className="font-medium">
                        <Link
                          href={ap(`/surveys/${survey.id}`)}
                          className="link link-hover"
                        >
                          {survey.name}
                        </Link>
                        {survey.demo && (
                          <span className="badge badge-info badge-soft badge-sm ms-2">Sample</span>
                        )}
                      </td>
                      <td className="text-base-content/70">
                        {survey.programme_name}
                      </td>
                      <td className="text-base-content/70">
                        {SURVEY_TYPE_LABELS[survey.survey_type] ?? survey.survey_type}
                      </td>
                      <td>
                        <span className={STATUS_BADGE[survey.status]}>
                          {STATUS_LABEL[survey.status]}
                        </span>
                      </td>
                      <td className="text-end tabular-nums">
                        {survey.response_count}
                      </td>
                      <td className="text-base-content/70">
                        {formatDate(survey.created_at)}
                      </td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <Link
                            href={ap(`/surveys/${survey.id}`)}
                            className="btn btn-square btn-ghost btn-sm"
                            title="View survey"
                          >
                            <span className="iconify lucide--eye size-4" />
                          </Link>
                          <Link
                            href={ap(`/surveys/${survey.id}/edit`)}
                            className="btn btn-square btn-ghost btn-sm"
                            title="Edit survey"
                          >
                            <span className="iconify lucide--pencil size-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="card bg-base-100">
            <div className="card-body items-center text-center py-16">
              <span className="iconify lucide--clipboard-list size-16 text-base-content/20 mb-4" />
              <h2 className="text-lg font-semibold">No surveys yet</h2>
              <p className="text-base-content/50 max-w-sm">
                Create your first impact survey to start collecting feedback
                from programme participants.
              </p>
              <div className="mt-6">
                <Link href={ap('/surveys/new')} className="btn btn-primary">
                  <span className="iconify lucide--plus size-4" />
                  Create your first survey
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </AccountLayout>
  )
}
